import { EnvironmentUtils } from "@rsm-hcd/javascript-core";
import Button, { ButtonTypes } from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { ImagePosition } from "constants/notebooks/image-position";
import { ImageSize } from "constants/notebooks/image-size";
import { ImageAnnotationRecord } from "models/view-models/block-configurations/image-annotation-record";
import FileRecord from "models/view-models/file-record";
import { ListBoxItemClassName } from "molecules/lists/list-box";
import ImageBlockImageDisplay from "molecules/page-block-content/image-block/image-block-image-display";
import FileUploadProgressBar from "molecules/progress-bars/file-upload-progress-bar";
import React, {
    Dispatch,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { S3Response } from "react-s3-uploader";
import { CollectionUtils } from "utilities/collection-utils";
import { CoreUtils } from "utilities/core-utils";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import FileUtils from "utilities/file-utils";
import { t } from "utilities/localization-utils";
import RemoteAccessDetailsService from "utilities/services/remote-access-details-service";
import StringUtils from "utilities/string-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import uuid from "uuid";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface ImageUploadFieldProps {
    replaceImageMode?: boolean;
    setReplaceImageMode?: Dispatch<React.SetStateAction<boolean>>;
    onClearSelectedImage?: () => void;
    accept?: string | Array<string>;
    /**
     * Return false to abort the upload to S3.
     * This callback gives you an opportunity to perform
     * validation on the file before uploading it.
     * @param file
     */
    beforeUpload?: (file: File) => Promise<boolean>;
    /**
     * Customize the confirm button text in the delete confirmation modal
     * @default "Yes, Remove"
     */
    confirmButtonText?: string;
    /**
     * Customize the text in the delete confirmation modal
     * @default "Are you sure you want to remove this file?"
     */
    confirmModalText?: string;
    /**
     * Show a confirmation dialog before calling onFileChanged(undefined)
     * @default false
     */
    confirmRemove?: boolean;
    /**
     * If true, will console.log updates about the upload progress,
     * only if in the development environment.
     */
    debugS3UploadProgress?: boolean;
    /**
     * Allows you to show loading file deletion state
     * when deleted from outside this component, for example
     * when closing the solution resource modal with the
     * cancel button.
     */
    deleteLoading?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    errorMessages?: Array<string>;
    fileUploadDestination?: FileUploadDestination;
    /**
     * Text to render under the "attach file" label.
     * You can use this to display instructions such as
     * a maximum file size or maximum image dimensions.
     */
    helpText?: string;
    isErrored?: boolean;
    /**
     * Max file size to accept, in Bytes.
     * You can use FileUtils#convertMbToBytes
     * if you want to specify size in MB
     */
    maxSize?: number;
    onFileChanged?: (file?: FileRecord) => void;
    onRetryUpload?: () => void;
    onUploadError?: (message: string) => void;
    onUploadStart?: () => void;
    position?: ImagePosition;
    required?: boolean;
    size?: ImageSize;
    /**
     * Used, for example, to put the publication ID in the relative path.
     */
    uploadPathPrefix?: string;
    value?: FileRecord;
    userUploadId: string;
    renderPreview?: boolean;
    /** Used to capture the coordinates of a click on the image to place annotations */
    onImageClick?: (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;
    onImageAnnotationSaved: (
        updatedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    onImageAnnotationRemoved: (
        removedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    onImageAnnotationMoved: (
        removedImageAnnotation: ImageAnnotationRecord | null | undefined
    ) => Promise<void>;
    imageAnnotations?: Array<ImageAnnotationRecord>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ImageUploadField: React.FC<ImageUploadFieldProps> = (
    props: ImageUploadFieldProps
) => {
    const {
        replaceImageMode = false,
        setReplaceImageMode,
        onClearSelectedImage,
        beforeUpload,
        debugS3UploadProgress,
        fileUploadDestination,
        isErrored,
        maxSize,
        onFileChanged,
        onRetryUpload,
        onUploadError,
        onUploadStart,
        uploadPathPrefix,
        disabled,
        deleteLoading,
        value,
        helpText,
        accept,
        errorMessage,
        errorMessages,
        userUploadId,
        size,
        position,
        onImageClick,
        onImageAnnotationMoved,
        onImageAnnotationRemoved,
        onImageAnnotationSaved,
        imageAnnotations,
    } = props;

    const CSS_BASE_CLASS = "c-image-block-upload-field";
    const cssClassNames = [CSS_BASE_CLASS, "c-form-field"];
    if (isErrored) {
        cssClassNames.push("-error");
    }

    const [imageFilePath, setImageFilePath] = useState<string>("");
    const uploadFileRef = useRef<HTMLInputElement>(null);
    const [replacementImageSelected, setReplacementImageSelected] =
        useState(false);

    useEffect(() => {
        setInputFile(undefined);
    }, [onClearSelectedImage]);

    useEffect(() => {
        if (replaceImageMode) {
            setShouldTriggerUpload(true);
            if (setReplaceImageMode) setReplaceImageMode(false);
            return;
        }
        if (value == null) {
            setImageFilePath("");
            return;
        }

        (async () => {
            const remoteAccessDetailsResult =
                await RemoteAccessDetailsService.get(undefined, {
                    storageContainer: value.storageContainer,
                    relativeProviderPath: value.relativeProviderPath,
                });

            setImageFilePath(remoteAccessDetailsResult.resultObject?.url ?? "");
        })();
    }, [replaceImageMode, setReplaceImageMode, value]);

    const onFinishUploading = useCallback(
        async (uploadResult: S3Response, file?: FileRecord) => {
            if (!isErrored) {
                // show the success state before transitioning
                await CoreUtils.sleep(500);
            }

            if (value != null) {
                FileUtils.deleteFile(value);
            }
            setInputFile(undefined);
            setProgress(0);

            if (file == null) {
                onFileChanged?.(file);
                return;
            }

            // Presign the url with the GET verb for caching
            const remoteAccessDetailsResult =
                await RemoteAccessDetailsService.get(undefined, {
                    storageContainer: file.storageContainer,
                    relativeProviderPath: file.relativeProviderPath,
                });

            setImageFilePath(remoteAccessDetailsResult.resultObject?.url ?? "");
            const uploadedFile = file.with({
                presignedUrl: remoteAccessDetailsResult.resultObject?.url,
                suggestedCode: file.suggestedCode,
                suggestedEdition: file.suggestedEdition,
                suggestedLocale: file.suggestedLocale,
            });

            onFileChanged?.(uploadedFile);
            //Keep showing the preview image until the save is complete.
            setPreviewUrl(null);
        },
        [isErrored, value, onFileChanged]
    );

    const handleUploadError = useCallback(
        (message: string) => {
            ToastManager.error(message);
            onUploadError?.(message);
        },
        [onUploadError]
    );

    const handleProgress = useCallback(
        (percent: number, status: string, file: File) => {
            if (debugS3UploadProgress === true) {
                EnvironmentUtils.runIfDevelopment(() =>
                    console.log(
                        `S3-UPLOAD:: ${file.name}: ${percent}% - ${status}`
                    )
                );
            }
            setProgress(percent);
        },
        [debugS3UploadProgress]
    );

    const handleRetry = () => inputFile != null && uploadFile(inputFile);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!imageFile) {
            setPreviewUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(imageFile);
        setPreviewUrl(objectUrl);

        // Cleanup: revoke the object URL when component unmounts or file changes
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null;
        if (selectedFile == null) return;
        setReplacementImageSelected(true);

        //User did NOT click cancel on the file explorer dialog.
        //Proceed to delete the old file if there is one.
        if (value != null) {
            FileUtils.deleteFile(value);
        }

        setImageFile(null);
        onFileChanged?.(undefined);

        if (previewUrl == null) {
            if (selectedFile && selectedFile.type.startsWith("image/")) {
                setImageFile(selectedFile);
            }
            return;
        }
        if (e.target.files != null && e.target.files.length > 0) {
            uploadFile(e.target.files[0]);
        }
    };

    const handleFileRemoved = async () => {
        if (previewUrl != null) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            return;
        }
    };

    const [inputFile, setInputFile] = useState<File | undefined>();
    const [progress, setProgress] = useState<number>(0);
    const [shouldTriggerUpload, setShouldTriggerUpload] = useState(false);

    const handleUploadStart = useCallback(
        async (file: File, next?: (file: File) => any): Promise<boolean> => {
            setInputFile(file);
            setProgress(0);
            next?.(file);

            if (beforeUpload != null) {
                const propValidateResult = await beforeUpload(file);
                if (!propValidateResult) {
                    return false;
                }
            }

            if (maxSize != null && file.size > maxSize) {
                ToastManager.error(
                    t("file-upload-file-too-large", {
                        size: FileUtils.bytesToMegabytes(maxSize),
                    })
                );
                return false;
            }

            onUploadStart?.();
            return true;
        },
        [beforeUpload, maxSize, onUploadStart]
    );

    const handleUploadAbort = useCallback(() => {
        setProgress(0);
        setInputFile(undefined);
        onFileChanged?.(undefined);
    }, [onFileChanged]);

    const uploadFile = useCallback(
        (file: File) => {
            onRetryUpload?.();
            FileUtils.uploadFile(file, {
                beforeUpload: handleUploadStart,
                fileUploadDestination,
                onAbort: handleUploadAbort,
                onError: handleUploadError,
                onFinish: onFinishUploading,
                onProgress: handleProgress,
                uploadPathPrefix,
                bucketPrefix: userUploadId,
            });
        },
        [
            fileUploadDestination,
            handleProgress,
            handleUploadAbort,
            handleUploadError,
            handleUploadStart,
            onFinishUploading,
            onRetryUpload,
            uploadPathPrefix,
            userUploadId,
        ]
    );

    const handleDrop = useCallback(
        (acceptedFiles: Array<File>, rejectedFiles: Array<FileRejection>) => {
            if (CollectionUtils.hasValues(acceptedFiles)) {
                if (previewUrl == null) {
                    setImageFile(acceptedFiles[0]);
                    return;
                }
            }

            if (CollectionUtils.hasValues(rejectedFiles)) {
                if (rejectedFiles.length > 1) {
                    ToastManager.error(t("file-upload-only-one-file-allowed"));
                    return;
                }

                ToastManager.error(
                    `${rejectedFiles.length} file(s) failed to upload.`
                );
            }
        },
        [previewUrl]
    );

    const getFileContainerClassNames = (): string => {
        const classNames = [
            ListBoxItemClassName,
            `${CSS_BASE_CLASS}__file-container__progress`,
        ];
        if (progress >= 100 && !isErrored) {
            classNames.push("-success");
        }

        if (isErrored) {
            classNames.push("-error");
        }

        return classNames.join(" ");
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: accept,
        disabled: disabled,
        multiple: false,
        onDrop: handleDrop,
    });

    const handleFileUpload = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): void => {
        if (previewUrl == null) return;
        if (imageFile == null) return;

        uploadFile(imageFile);
    };

    useEffect(() => {
        if (shouldTriggerUpload) {
            setPreviewUrl(null);
            uploadFileRef.current?.click();
            setShouldTriggerUpload(false);
        }
    }, [shouldTriggerUpload, uploadFileRef]);

    return (
        <>
            {/* Hidden file input to trigger file dialog on replace image button click */}
            <input
                type="file"
                ref={uploadFileRef}
                onChange={handleFileInputChange}
                accept={getInputProps().accept}
                style={{ display: "none" }}
                tabIndex={-1}
            />
            {value == null && inputFile == null && previewUrl == null && (
                <div
                    className={`${CSS_BASE_CLASS}__file-container`}
                    {...getRootProps()}>
                    <input
                        onChange={handleFileInputChange}
                        {...getInputProps()}
                    />
                    {
                        // if
                        isDragActive && <p>Drop the files here</p>
                    }
                    {
                        // if
                        !isDragActive && (
                            <div
                                className={`${CSS_BASE_CLASS}__file-container__upload-actions`}>
                                <Button>
                                    <Icon
                                        type={Icons.ImageUpload}
                                        size={IconSizes.Medium}></Icon>
                                    <span className="upload-image-button-text">
                                        {t("file-upload-upload-image")}
                                    </span>
                                </Button>
                                <label className="-help-text">{helpText}</label>
                            </div>
                        )
                    }
                </div>
            )}
            {
                <>
                    {(previewUrl || imageFilePath) && (
                        <ImageBlockImageDisplay
                            onImageAnnotationRemoved={onImageAnnotationRemoved}
                            isEditable={true}
                            imageFilePath={previewUrl ?? imageFilePath}
                            size={size}
                            position={position}
                            altText=""
                            onClick={onImageClick}
                            onImageAnnotationSaved={onImageAnnotationSaved}
                            onImageAnnotationMoved={onImageAnnotationMoved}
                            imageAnnotations={imageAnnotations}
                        />
                    )}

                    {(value != null || previewUrl != null) && (
                        <React.Fragment>
                            {
                                // if
                                value == null && inputFile != null && (
                                    <div
                                        className={getFileContainerClassNames()}>
                                        <FileUploadProgressBar
                                            cssClassName={`${CSS_BASE_CLASS}__file-container__progress__progress-bar`}
                                            isErrored={isErrored}
                                            onRetryClick={handleRetry}
                                            value={progress}
                                        />
                                    </div>
                                )
                            }
                            {(value == null || replacementImageSelected) &&
                                previewUrl !== null && (
                                    <div
                                        className={`${ListBoxItemClassName} ${CSS_BASE_CLASS}__file-container__action-buttons`}>
                                        <Button
                                            accessibleText={t(
                                                "file-upload-clear-button"
                                            )}
                                            cssClassName={`${CSS_BASE_CLASS}__file-container__action-buttons-clear`}
                                            disabled={disabled || deleteLoading}
                                            onClick={handleFileRemoved}
                                            size={ButtonSizes.Medium}
                                            type={ButtonTypes.Button}>
                                            {t("file-upload-clear-button")}
                                        </Button>
                                        <Button
                                            accessibleText={t(
                                                "file-upload-done-button"
                                            )}
                                            cssClassName={`${CSS_BASE_CLASS}__file-container__action-buttons-done`}
                                            disabled={disabled || deleteLoading}
                                            onClick={handleFileUpload}
                                            size={ButtonSizes.Medium}
                                            type={ButtonTypes.Button}>
                                            {t("file-upload-done-button")}
                                        </Button>
                                    </div>
                                )}
                        </React.Fragment>
                    )}
                </>
            }
            {
                // if
                (CollectionUtils.hasValues(errorMessages) ||
                    StringUtils.hasValue(errorMessage)) && (
                    <div
                        className={`${CSS_BASE_CLASS}__errors-container c-form-field__bottom__errors`}>
                        {errorMessages?.map((s: string) => (
                            <label key={uuid.v4()}>{s}</label>
                        ))}
                        {StringUtils.hasValue(errorMessage) && (
                            <label>{errorMessage}</label>
                        )}
                    </div>
                )
            }
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default ImageUploadField;

// #endregion Exports
